/**
 * Геометрия комментария-выноски: размер рамки по тексту, откуда выходит стрелка
 * и куда она указывает, если комментарий привязан к объекту; чей он слой.
 *
 * Всё в миллиметрах плана, но кегль и отступы задаются в экранных пикселях и
 * умножаются на `mmPerPx` — как у подписей комнат, выноска не меняется при зуме.
 * Ширину строки меряем приближённо, по средней ширине буквы: SVG-текст до отрисовки
 * не измерить, а рамке достаточно не обрезать текст.
 */
import { add, angleOf, sub, type Pt } from "@/shared/lib/geometry";
import {
	LayerKind,
	getPlanLayer,
	type CommentTarget,
	type CommentTargetKind,
	type PlanComment,
	type PlanLayers,
} from "@/shared/plan";
import { fixturePlanPoint, pointAlongWall } from "@/widgets/plan";

/** Средняя ширина буквы в долях кегля — для кириллицы в системном шрифте. */
const CHAR_WIDTH = 0.56;
/** Межстрочный интервал в долях кегля. */
export const COMMENT_LINE_HEIGHT = 1.3;
/** Отступ текста от рамки, экранных пикселей. */
const PADDING_PX = 6;
/** Самая узкая рамка в буквах: пустой комментарий, пока в нём пишут, не схлопывается. */
const MIN_CHARS = 12;

export interface CommentBox {
	/** Левый верхний угол рамки, мм */
	x: number;
	y: number;
	width: number;
	height: number;
	/** Строки текста; пустой комментарий — одна пустая строка, чтобы рамка не схлопнулась */
	lines: string[];
	/** Кегль, мм */
	fontSize: number;
	/** Отступ текста от рамки, мм */
	padding: number;
}

export const commentBox = (comment: PlanComment, mmPerPx: number): CommentBox => {
	const fontSize = comment.fontSize * mmPerPx;
	const padding = PADDING_PX * mmPerPx;
	const lines = comment.text.split("\n");
	const longest = Math.max(MIN_CHARS, ...lines.map((line) => line.length));
	const width = longest * fontSize * CHAR_WIDTH + padding * 2;
	const height = lines.length * fontSize * COMMENT_LINE_HEIGHT + padding * 1.4;
	return {
		x: comment.box.x - width / 2,
		y: comment.box.y - height / 2,
		width,
		height,
		lines,
		fontSize,
		padding,
	};
};

/**
 * Где стрелка выходит из рамки: точка на её крае по направлению к `anchor`.
 * null — стрелка не нужна: точка внутри рамки.
 */
export const pointerStart = (box: CommentBox, anchor: Pt): Pt | null => {
	const cx = box.x + box.width / 2;
	const cy = box.y + box.height / 2;
	const dx = anchor.x - cx;
	const dy = anchor.y - cy;
	const halfW = box.width / 2;
	const halfH = box.height / 2;
	if (Math.abs(dx) <= halfW && Math.abs(dy) <= halfH) return null;
	// Во сколько раз укоротить отрезок центр → точка, чтобы он кончился на крае рамки
	const scale = Math.min(
		dx === 0 ? Infinity : halfW / Math.abs(dx),
		dy === 0 ? Infinity : halfH / Math.abs(dy),
	);
	return { x: cx + dx * scale, y: cy + dy * scale };
};

/** Куда встаёт рамка нового комментария: вправо-вверх от точки клика, в экранных пикселях. */
export const NEW_COMMENT_OFFSET_PX = { x: 80, y: -50 } as const;
/** На сколько ниже, экранных пикселей, встаёт каждый следующий комментарий того же объекта. */
export const COMMENT_STACK_PX = 34;

/** Точка плана -> пиксели внутри холста (холст и вьюпорт одних пропорций). */
export const planToCanvasPx = (
	point: Pt,
	viewBox: { x: number; y: number },
	mmPerPx: number,
): Pt => ({ x: (point.x - viewBox.x) / mmPerPx, y: (point.y - viewBox.y) / mmPerPx });

/* ─── Привязка к объекту ─── */

/** Оси объекта на плане: от какой точки меряется смещение и как повёрнуты оси, рад. */
interface TargetFrame {
	origin: Pt;
	angle: number;
}

const rotate = (p: Pt, angle: number): Pt => ({
	x: p.x * Math.cos(angle) - p.y * Math.sin(angle),
	y: p.x * Math.sin(angle) + p.y * Math.cos(angle),
});

const DEG = Math.PI / 180;

/** Прибор на стене любого слоя: электрика, сантехника, мебель на стене (картина, ТВ) */
const findFixture = (layers: PlanLayers, id: string) =>
	layers.electrical.fixtures[id] ?? layers.plumbing.fixtures[id] ?? layers.furniture.fixtures[id];

/** Оси объекта; null — объекта больше нет (удалили, стену разрезали). */
const targetFrame = (
	layers: PlanLayers,
	kind: CommentTargetKind,
	id: string,
): TargetFrame | null => {
	const graph = layers.walls;
	switch (kind) {
		case "item": {
			const item = layers.furniture.items[id] ?? layers.plumbing.items[id];
			return item ? { origin: { x: item.x, y: item.y }, angle: item.rotation * DEG } : null;
		}
		case "light": {
			const light = layers.electrical.items[id];
			return light ? { origin: { x: light.x, y: light.y }, angle: light.rotation * DEG } : null;
		}
		case "fixture": {
			const fixture = findFixture(layers, id);
			const point = fixture ? fixturePlanPoint(graph, fixture) : null;
			return point ? { origin: point, angle: 0 } : null;
		}
		case "opening": {
			const opening = graph.openings[id];
			const wall = opening ? graph.walls[opening.wallId] : undefined;
			const a = wall ? graph.nodes[wall.a] : undefined;
			const b = wall ? graph.nodes[wall.b] : undefined;
			if (!opening || !a || !b) return null;
			return { origin: pointAlongWall(a, b, opening.offset), angle: 0 };
		}
		case "wall": {
			const wall = graph.walls[id];
			const a = wall ? graph.nodes[wall.a] : undefined;
			const b = wall ? graph.nodes[wall.b] : undefined;
			if (!a || !b) return null;
			return { origin: a, angle: angleOf(sub(b, a)) };
		}
	}
};

/**
 * Куда указывает стрелка: у привязанного — в точку на объекте, где бы он сейчас ни стоял,
 * у свободного или потерявшего объект — в сохранённую точку плана.
 */
export const commentAnchor = (comment: PlanComment, layers: PlanLayers): Pt => {
	const target = comment.target;
	const frame = target ? targetFrame(layers, target.kind, target.id) : null;
	if (!target || !frame) return comment.anchor;
	return add(frame.origin, rotate(target.offset, frame.angle));
};

/** Привязка к объекту, на который стрелка указывает в точке `point`; null — объекта нет. */
export const commentTargetAt = (
	layers: PlanLayers,
	kind: CommentTargetKind,
	id: string,
	point: Pt,
): CommentTarget | null => {
	const frame = targetFrame(layers, kind, id);
	if (!frame) return null;
	return { kind, id, offset: rotate(sub(point, frame.origin), -frame.angle) };
};

/**
 * Привязка для нового комментария к объекту из панели свойств: стрелка — в его точку,
 * у стены — в середину. null — объекта нет.
 */
export const defaultCommentTarget = (
	layers: PlanLayers,
	kind: CommentTargetKind,
	id: string,
): { target: CommentTarget; anchor: Pt } | null => {
	const frame = targetFrame(layers, kind, id);
	if (!frame) return null;
	let offset: Pt = { x: 0, y: 0 };
	if (kind === "wall") {
		const wall = layers.walls.walls[id];
		const a = wall && layers.walls.nodes[wall.a];
		const b = wall && layers.walls.nodes[wall.b];
		if (a && b) offset = { x: Math.hypot(b.x - a.x, b.y - a.y) / 2, y: 0 };
	}
	const target = { kind, id, offset };
	return { target, anchor: add(frame.origin, rotate(offset, frame.angle)) };
};

/**
 * Слой, которому принадлежит комментарий, — слой его объекта. null — комментарий свободный
 * (или объекта уже нет): такой виден всегда.
 */
export const commentLayer = (comment: PlanComment, layers: PlanLayers): LayerKind | null => {
	const target = comment.target;
	if (!target || !targetFrame(layers, target.kind, target.id)) return null;
	switch (target.kind) {
		case "item":
			return target.id in layers.furniture.items ? LayerKind.Furniture : LayerKind.Plumbing;
		case "light":
			return LayerKind.Electrical;
		case "fixture":
			return target.id in layers.electrical.fixtures
				? LayerKind.Electrical
				: target.id in layers.plumbing.fixtures
					? LayerKind.Plumbing
					: LayerKind.Furniture;
		case "opening":
		case "wall":
			return LayerKind.Walls;
	}
};

/** Виден ли комментарий: свободный — всегда, привязанный — пока виден слой его объекта. */
export const isCommentVisible = (comment: PlanComment, layers: PlanLayers): boolean => {
	const layer = commentLayer(comment, layers);
	return layer === null || getPlanLayer(layers, layer).visible;
};
